import React, { useState } from 'react';
import { ChevronDown, Sparkles, HelpCircle } from 'lucide-react';

const faqs = [
  {
    question: 'What is the difference between Infor CloudSuite Industrial (CSI) and legacy SyteLine?',
    answer:
      'Infor CloudSuite Industrial (CSI) is the modern cloud-native evolution of Infor SyteLine. Delivered as a secure, multi-tenant SaaS application on Amazon Web Services (AWS), Infor CSI features the Infor OS digital platform, Infor ION enterprise integration, Coleman AI, mobile shop-floor applications, and automatic monthly updates, eliminating costly traditional hardware maintenance and multi-year upgrade cycles.',
  },
  {
    question: 'How does PT KCS approach migration from on-premise SyteLine 7/8/9 to Infor CSI on AWS?',
    answer:
      'We utilize a phased, risk-mitigated migration framework. Our architects begin with an exhaustive audit of your legacy schema extensions, database tables, and third-party integrations. Custom code is converted to upgrade-safe Infor Mongoose extensions, historical data is cleansed and validated through automated extractors, and parallel simulation runs are performed to ensure 100% operational continuity prior to cutover.',
  },
  // {
  //   question: 'Can Infor CSI handle mixed-mode manufacturing (ETO, MTO, and MTS) in a single plant?',
  //   answer:
  //     'Yes. Infor CSI is specifically architected for complex mixed-mode environments. In a single plant, you can manufacture repetitive make-to-stock items using production schedules, configure complex custom assemblies via the rules-based Product Configurator, and execute full engineer-to-order (ETO) project tracking with synchronized CAD/PLM BOMs.',
  // },
  // {
  //   question: 'How does Infor ION facilitate integrations with third-party software (CRM, WMS, CAD, PLM)?',
  //   answer:
  //     'Infor Intelligent Open Network (ION) operates as an advanced enterprise service bus (ESB) using open OAGIS XML standards. It decouples core ERP code from surrounding systems, allowing bi-directional, event-driven data flow between Infor CSI and platforms like Salesforce, Siemens Teamcenter, PTC Windchill, dedicated WMS systems, and industrial IoT machine sensors without point-to-point fragility.',
  // },
  // {
  //   question: 'What is the typical implementation timeline and agile methodology used by PT KCS?',
  //   answer:
  //     'By applying PT KCS proprietary accelerators, pre-built industry templates, and agile sprint cycles, most mid-market to enterprise deployments are achieved in 4 to 8 months. Our methodology breaks delivery into five disciplined stages: Strategic Discovery & Fit-Gap, Solution Blueprinting, Sprint-Based System Configuration, User Acceptance Testing (UAT) Simulation, and Go-Live Cutover with Hypercare.',
  // },
  {
    question: 'How do you preserve custom workflows and screens across automated Infor Cloud updates?',
    answer:
      'All extensions and business logic are engineered strictly within the Infor Mongoose metadata framework. Mongoose completely isolates custom screens, scripts, and validation rules from core application binaries. As Infor pushes continuous functional enhancements and security patches to the cloud, your custom workflows and data integrity remain 100% intact.',
  },
  {
    question: 'How does Infor CSI Advanced Planning & Scheduling (APS) differ from traditional MRP?',
    answer:
      'Traditional MRP operates on infinite capacity assumptions, requiring manual re-scheduling when work centers or tooling become overloaded. Infor CSI APS is a true finite-capacity engine that simultaneously evaluates machine capacity, labor shifts, tooling availability, and material lead times to generate dynamically executable production schedules and accurate customer delivery promises (CTP).',
  },
  {
    question: 'What 24/7 ongoing support and SLA commitments does PT KCS offer after go-live?',
    answer:
      'Our global follow-the-sun support model provides dedicated tier-3 functional and technical consultants across the Americas, EMEA, and APAC. We provide guaranteed SLA-backed response times, proactive system health audits, regression testing for quarterly Infor cloud updates, continuous user coaching, and managed enhancement backlogs.',
  },
];

const CSIFAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const toggleFaq = (index) => {
    setOpenIndex((currentIndex) => (currentIndex === index ? null : index));
  };

  return (
    <section
      id="csi-faq"
      className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      <div className="mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-6xl mx-auto mb-16">

          <h2
            className="font-satoshi text-2xl font-bold leading-tight sm:text-3xl md:text-4xl"
            style={{ color: '#0F172A', fontFamily: "'Satoshi', sans-serif" }}
          >
            Frequently Asked Questions
          </h2>

          <p
            className="text-center mt-4 text-base sm:text-lg leading-relaxed"
            style={{ color: '#475569' }}
          >
            Clear insights into Infor CloudSuite Industrial architecture, migration pathways, 
            integration patterns, and delivery methodology.
          </p>
        </div>

        {/* FAQ Accordion List */}
        <div className="mx-auto max-w-4xl space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;

            return (
              <div
                key={faq.question}
                className="overflow-hidden rounded-2xl border transition-all duration-300"
                style={{
                  backgroundColor: isOpen ? '#F8FAFC' : '#FFFFFF',
                  borderColor: isOpen ? '#1D4ED8' : '#E2E8F0',
                  boxShadow: isOpen
                    ? '0 10px 30px rgba(29, 78, 216, 0.08)'
                    : '0 2px 8px rgba(15, 23, 42, 0.03)',
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleFaq(index)}
                  className="flex w-full items-center justify-between gap-4 p-6 text-left transition-colors duration-200"
                  aria-expanded={isOpen}
                >
                  <span
                    className="text-base sm:text-lg font-bold transition-colors duration-200"
                    style={{ color: isOpen ? '#1D4ED8' : '#0F172A' }}
                  >
                    {faq.question}
                  </span>
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-all duration-300 border"
                    style={{
                      backgroundColor: isOpen ? '#1D4ED8' : '#F1F5F9',
                      color: isOpen ? '#FFFFFF' : '#64748B',
                      borderColor: isOpen ? '#1D4ED8' : '#E2E8F0',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  >
                    <ChevronDown className="h-5 w-5" />
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div
                    className="px-6 pb-6 pt-1 text-sm sm:text-base leading-relaxed border-t"
                    style={{
                      color: '#475569',
                      borderColor: isOpen ? '#E2E8F0' : 'transparent',
                    }}
                  >
                    {faq.answer}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CSIFAQ;
